import { useApolloClient } from '@apollo/client';
import { ButtonWidth } from '@leland-dev/leland-ui-library';
import { type StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { type DocumentNode } from 'graphql';
import { type FC, useEffect, useState } from 'react';

import { formatStripeErrorMessage } from '../../../../utils/checkout';
import SvgIcon, { Svg } from '../../../SvgIcon';
import { StripeCardElement } from '../../../stripe/StripeCardElement';
import { StripeProvider } from '../../../stripe/StripeProvider';
import { useSaveCardInfo } from '../../../stripe/useSaveCardInfo';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';

interface PaymentDetailsProps extends BaseScheduleSubmissionModalLayoutProps {
  onMoveToSubmissionStep: () => void;
  refetchQueries?: DocumentNode[];
  hasSavedCards: boolean;
}

export const PaymentDetailsContent: FC<PaymentDetailsProps> = ({
  onMoveToSubmissionStep,
  refetchQueries,
  hasSavedCards,
  ...baseScheduleModalProps
}) => {
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [error, setError] = useState<Nullable<string>>(null);
  const apolloClient = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (hasSavedCards) {
      onMoveToSubmissionStep();
    }
  }, [hasSavedCards, onMoveToSubmissionStep]);

  const handleCardElementChange = (event: StripeCardElementChangeEvent) => {
    if (isCardComplete !== event.complete) {
      setIsCardComplete(event.complete);
    }

    if (event.error) {
      setError(formatStripeErrorMessage(event.error.message));
    } else {
      setError(null);
    }
  };

  const saveCardInfo = useSaveCardInfo();

  const onNextStep = async () => {
    setIsLoading(true);
    const paymentMethod = await saveCardInfo(setError);
    if (paymentMethod) {
      await apolloClient.refetchQueries({ include: refetchQueries });
      onMoveToSubmissionStep();
    }
    setIsLoading(false);
  };

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        onClick: () => void onNextStep(),
        disabled: !isCardComplete || !!error || isLoading,
        width: ButtonWidth.FULL_MOBILE,
      }}
      preventScroll
      sidebarOptions={{
        title: 'Add your payment info',
        subtitle:
          'Future sessions will be scheduled using your available time balance. If your balance is insufficient, you\u2019ll be charged automatically when you join the session.',
      }}
      {...baseScheduleModalProps}
    >
      <div className="w-full p-8">
        <h2 className="text-lg font-medium">Card details</h2>
        <p className="text-leland-gray-light">
          $0 due today. You won’t be charged until your first session starts.
        </p>
        <StripeCardElement onChange={handleCardElementChange} />
        <div className="mt-2 flex items-center justify-end">
          <SvgIcon svgType={Svg.POWERED_BY_STRIPE} />
        </div>
        {error ? <p className="text-red-500">{error}</p> : null}
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};

export const PaymentDetails: FC<PaymentDetailsProps> = (props) => {
  return (
    <StripeProvider>
      <PaymentDetailsContent {...props} />
    </StripeProvider>
  );
};
